import React, { useEffect } from "react";
import { getFirestore, doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { app } from "./firebase"; // your Firebase init
import HomePage from './Home_page';
import Signup from './Get_started';
import LoginPage from './Login_page';
import Dashboard from './HomeScreen';
import ForgetPassword from './Forget_password';
import { Route, BrowserRouter as Router, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Profile from "./ProfilePage";
import Add from "./pages/Add";
import TitleAndTagCompetition from './pages/TitleAndTagCompetition';
import ViewIdeaPage from "./pages/ViewIdeaPage";
import ViewRewardedIdea from "./ViewRewardedIdea";
import Leaderboard from "./pages/Leaderboard";
import Grid from "./pages/Dashboard";
import TitleAndTag from './pages/TitleAndTag';
import PrivateRoute from "./PrivateRoute";
import DescriptionPage from './pages/Description';
import UploadImage from './pages/Upload-Image';
// import AIGenerator from './pages/AIGenerator'; // Temporarily disabled for future use
import PreviewPage from './pages/PreviewPage';
import AdminRoute from "./components/AdminRoute";
import AdminInboxPage from "./pages/AdminInboxPage";
import UserInboxPage from "./pages/UserInboxPage";
import ExplorePage from './pages/ExplorePage';
import PreviewCompetitionPage from './pages/PreviewPageCompetition';
import CategoryPage from "./CategoryPage"; // Reusable for all
import DescriptionPageCompetition from './pages/DescriptionCompetition';
import UploadImageCompetition from './pages/UploadImageCompetition';
import ViewLeaderboardIdeaPage from './pages/ViewLeaderboardIdeaPage';
import PostSuccess from './pages/PostSuccess';
import CompetitionSuccess from './pages/CompetitionSuccess';
import WalletPage from './pages/WalletPage';
import PayoutRequestPage from './pages/PayoutRequestPage';
import AdminEarningsPage from './pages/AdminEarningsPage';
import JourneyPage from './pages/JourneyPage'; // ✅ Journey Page
import SearchPeoplePage from './pages/SearchPeoplePage'; // ✅ Search People Page
import SavedPage from './pages/SavedPage'; // ✅ Saved Page
import SettingsPage from './pages/SettingsPage'; // ✅ Settings Page
import EvolvePage from './pages/EvolvePage'; // ✅ Evolve Page
import KingdomPage from './pages/KingdomPage'; // 🏰 Kingdom (NEW)
import { useAuth } from "./authContext";
import AssetPreloader from './components/AssetPreloader'; // ✅ Preload icons for instant navigation
import UsernameGate from "./UsernameGate";
import ConsentGate from "./ConsentGate";
import TutorPremiumPage from './pages/TutorPremiumPage';
import TutorChatPage from './pages/TutorChatPage';
import TopSparkView from "./TopSparkView";

// Public info pages (needed for AdSense review)
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import PublicFeed from "./PublicFeed";

// Mascot
import { MascotProvider } from './mascot/MascotContext';

const db = getFirestore(app);

// Loading spinner component
const RedirectToLoginWithState = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/Login_page", { state: { from: location.pathname }, replace: true });
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigate, location]);

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#000',
      color: '#fff'
    }}>
      <div style={{ marginBottom: '20px', fontSize: '1.2rem' }}>Redirecting to Login...</div>
      <div style={{
        border: '3px solid #333',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#1a1a1a',
    color: 'white'
  }}>
    <div style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 1s linear infinite'
    }}></div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  const { currentUser, loading } = useAuth();

  // THE FIX: a user only counts as "logged in" if their email is verified
  // (Google accounts are already verified by Google).
  const isVerified =
    !!currentUser &&
    (currentUser.emailVerified ||
      currentUser.providerData.some((p) => p.providerId === "google.com"));

  // Sync user data to Firestore when authenticated AND verified
  useEffect(() => {
    const syncUserData = async () => {
      if (!currentUser || !isVerified) return;

      try {
        // Check the session flag first so we skip the Firestore read entirely when already synced
        const hasSynced = sessionStorage.getItem(`synced-${currentUser.uid}`);
        if (hasSynced) return;

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        const existing = userSnap.exists() ? userSnap.data() : {};

        const userData = {
          uid: currentUser.uid,
          email: currentUser.email,
          authorName: existing.username || currentUser.displayName || currentUser.email || "Anonymous",
          lastSeen: serverTimestamp(),
        };

        if (!userSnap.exists() || !userSnap.data().role) {
          userData.role = "user";
        }

        await setDoc(userRef, userData, { merge: true });
        sessionStorage.setItem(`synced-${currentUser.uid}`, 'true');
      } catch (err) {
        console.error("Error syncing user data:", err);
      }
    };

    syncUserData();
  }, [currentUser, isVerified]);

  if (loading) {
    console.log("AUTH: Still loading authentication state");
    return <LoadingSpinner />;
  }

  if (isVerified) {
    return (
      <Router>
        <AssetPreloader />
        <UsernameGate />
        <ConsentGate />
        <MascotProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/HomeScreen" />} />
            <Route path="/Home_page" element={<Dashboard />} />
            <Route path="/Get_started" element={<Navigate to="/HomeScreen" />} />
            <Route path="/Login_page" element={<Navigate to="/HomeScreen" />} />
            <Route path="/HomeScreen" element={<Dashboard />} />
            <Route path="/ExplorePage" element={<ExplorePage />} />
            <Route path="/add" element={<PrivateRoute><Add /></PrivateRoute>} />
            <Route path="/grid" element={<Grid />} />
            <Route path="/PreviewPage" element={<PrivateRoute><PreviewPage /></PrivateRoute>} />
            <Route path="/Description" element={<PrivateRoute><DescriptionPage /></PrivateRoute>} />
            <Route path="/DescriptionCompetition" element={<PrivateRoute><DescriptionPageCompetition /></PrivateRoute>} />
            <Route path="/TitleAndTag" element={<PrivateRoute><TitleAndTag /></PrivateRoute>} />
            <Route path="/TitleAndTagCompetition" element={<PrivateRoute><TitleAndTagCompetition /></PrivateRoute>} />
            <Route path="/Upload-Image" element={<PrivateRoute><UploadImage /></PrivateRoute>} />
            <Route path="/UploadImageCompetition" element={<PrivateRoute><UploadImageCompetition /></PrivateRoute>} />
            <Route path="/admin-inbox" element={<AdminRoute><AdminInboxPage /></AdminRoute>} />
            <Route path="/admin-earnings" element={<AdminRoute><AdminEarningsPage /></AdminRoute>} />
            <Route path="/inbox" element={<UserInboxPage />} />
            <Route path="/explore/:category" element={<CategoryPage />} />
            <Route path='/PreviewCompetitionPage' element={<PrivateRoute><PreviewCompetitionPage /></PrivateRoute>} />
            <Route path="/rewarded-idea" element={<ViewRewardedIdea />} />
            <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/view-idea/:id" element={<ViewIdeaPage />} />
            <Route path="/top-spark/:id" element={<TopSparkView />} />
            <Route path="/view-leaderboard-idea" element={<ViewLeaderboardIdeaPage />} />
            <Route path="/post-success" element={<PostSuccess />} />
            <Route path="/competition-success" element={<CompetitionSuccess />} />
            <Route path="/wallet" element={<PrivateRoute><WalletPage /></PrivateRoute>} />
            <Route path="/payout-request" element={<PrivateRoute><PayoutRequestPage /></PrivateRoute>} />
            <Route path="/journey/:userId" element={<PrivateRoute><JourneyPage /></PrivateRoute>} />
            {/* 🏰 Kingdom (NEW): your own kingdom, and someone else's by user id */}
            <Route path="/kingdom" element={<PrivateRoute><KingdomPage /></PrivateRoute>} />
            <Route path="/kingdom/:userId" element={<PrivateRoute><KingdomPage /></PrivateRoute>} />
            <Route path="/search-people" element={<PrivateRoute><SearchPeoplePage /></PrivateRoute>} />
            <Route path="/saved" element={<PrivateRoute><SavedPage /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
            <Route path="/evolve/:id" element={<PrivateRoute><EvolvePage /></PrivateRoute>} />
            <Route path="/tutor/premium" element={<PrivateRoute><TutorPremiumPage /></PrivateRoute>} />
            <Route path="/tutor" element={<PrivateRoute><TutorChatPage /></PrivateRoute>} />

            {/* Public info pages */}
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </MascotProvider>
      </Router>
    );
  }

  console.log("AUTH: User is not authenticated (or not verified), rendering public routes");
  return (
    <Router>
      <AssetPreloader />
      <Routes>
        {/* Homepage renders directly at "/" (no redirect) so crawlers see it immediately */}
        <Route path="/" element={<HomePage />} />
        <Route path="/Home_page" element={<HomePage />} />
        <Route path="/Get_started" element={<Signup />} />
        <Route path="/Login_page" element={<LoginPage />} />
        <Route path="/Forget_password" element={<ForgetPassword />} />
        <Route path="/ExplorePage" element={<ExplorePage />} />
        <Route path="/grid" element={<Grid />} />
        <Route path="/explore/:category" element={<CategoryPage />} />
        <Route path="/rewarded-idea" element={<ViewRewardedIdea />} />
        <Route path="/view-idea/:id" element={<ViewIdeaPage />} />
        <Route path="/view-leaderboard-idea" element={<ViewLeaderboardIdeaPage />} />

        {/* Public feed: logged-out visitors and Googlebot see the real ideas here */}
        <Route path="/ideas" element={<PublicFeed />} />

        {/* Public info pages: must stay above the "*" catch-all */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        <Route
          path="/journey/:userId"
          element={<RedirectToLoginWithState />}
        />
        <Route path="/HomeScreen" element={<Navigate to="/Login_page" />} />
        <Route path="/add" element={<Navigate to="/Login_page" />} />
        <Route path="/profile" element={<Navigate to="/Login_page" />} />
        <Route path="/wallet" element={<Navigate to="/Login_page" />} />
        <Route path="/inbox" element={<Navigate to="/Login_page" />} />
        <Route path="/leaderboard" element={<Navigate to="/Login_page" />} />
        <Route path="*" element={<Navigate to="/Login_page" />} />
      </Routes>
    </Router>
  );
}
export default App;